import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Checkbox, DatePicker, Empty, Input, InputNumber, Modal, Select, Space, Spin, Statistic, Switch, Table, Tag, Tooltip, message,
} from 'antd';
import { CopyOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import TimezoneAwareTime from '../../components/TimezoneAwareTime';
import {
  confirmNonStdSettlement, getNonStdSettlement, previewNonStdSettlement, saveNonStdSettlement, searchNonStdSettlementUnits,
} from '../../services/nonstandardOps';
import {
  ContainerDraft, ContainerLedgerRow, SettlementActionRow, SettlementLine, SettlementOutput, SettlementOverview, SettlementPreview, SettlementUnitInfo,
} from '../../types/nonstandardOps';
import { containerTypeText } from './containerTypes';

/** 面板向外报告的状态：终审页据此显示标签，并在有未保存/未确认的修改时拦住"提交终审" */
export interface SettlementStatus {
  text: string;
  color: 'default' | 'success' | 'warning' | 'processing';
  /** 本地有还没保存的修改 */
  dirty: boolean;
  /** 草稿里有设置过的库存单元但还没有预览确认（提交终审会被后端拒绝） */
  needsConfirm: boolean;
  /** 供终审页底部的"预览并确认终审操作"栏使用：这个指令支持库存结算、已经应用、管理员能不能编辑、已确认（及确认时间）、有没有设置过处理（含已保存的容器） */
  supported: boolean;
  applied: boolean;
  editable: boolean;
  confirmed: boolean;
  confirmedAt: string | null;
  hasWork: boolean;
}

/** 面板对外提供的操作：终审页底部的确认栏用它来保存、同步和打开预览 */
export interface SettlementPanelHandle {
  /** 保存库存结算草稿（没有未保存的修改时直接返回 true）；失败返回 false */
  save: () => Promise<boolean>;
  /** 打开"终审操作预览"弹窗 */
  openPreview: () => Promise<void>;
  /** 从服务器同步最新的版本和确认状态（有未保存的修改时不覆盖正在编辑的内容） */
  refresh: () => Promise<void>;
}

interface Props {
  instructionId: number;
  /** 不支持结算（例如操作对象是 IOC）或没有权限时，显示原来的只读内容 */
  legacy: React.ReactNode;
  onStatus?: (status: SettlementStatus) => void;
  /** 草稿保存、确认或结果变化后，让外层刷新（容器面板据此重新读取对账表） */
  onChanged?: () => void;
  /** 终审容器分配保存后加 1：容器保存会让这里已有的预览确认作废，需要同步版本和确认状态 */
  reloadKey?: number;
  /** 已保存的容器数量：有容器草稿也算"有需要预览确认的内容" */
  containerCount?: number;
  /** 执行阶段的销毁记录合计（文字）：有记录时在处理表上方提醒，方便对照填写"销毁" */
  destroyNote?: string;
}

/** 一行内容物：产品名(分拣条码)×数量；有多个分拣条码时只写第一个，后面写"等N个条码" */
const contentLine = (it: { name: string; qty: number; barcodes?: string[] }) => {
  const codes = it.barcodes || [];
  const codeText = codes.length === 0 ? '' : `(${codes[0]}${codes.length > 1 ? ` 等${codes.length}个条码` : ''})`;
  return `${it.name}${codeText}×${it.qty}`;
};

/** 转化目标下拉里的一行内容项：产品名(编码:客户自定义编码 条码:分拣条码 等N个)×数量；没有的部分不写 */
const optionContentLine = (it: { name: string; qty: number; code?: string; barcodes?: string[] }) => {
  const codes = it.barcodes || [];
  const parts = [
    it.code ? `编码:${it.code}` : '',
    codes.length ? `条码:${codes[0]}${codes.length > 1 ? ` 等${codes.length}个` : ''}` : '',
  ].filter(Boolean);
  return `${it.name}${parts.length ? `(${parts.join(' ')})` : ''}×${it.qty}`;
};

/** 转化目标（带界面用的 key 和"手动库龄"开关） */
type Target = SettlementOutput & { key: string; age_manual?: boolean };

/**
 * 一个锁定的库存单元的处理方式。keep（不变）= 锁定数 − 转化 − 销毁，由前两者推出，不单独填。
 * group：转化目标挂在组上；默认每个库存单元自成一组（"u<id>"），批量"转化为同一个目标"时多个库存单元共用一个组（= 组合）。
 */
interface RowState {
  keep: number;
  convert: number;
  destroy: number;
  hold: number;
  group: string;
}

const ACTION_ROW_TEXT: Record<string, string> = {
  consume: '转消耗（扣减库存）', release: '释放锁定', reserve_hold: '建立业务留存占用', produce: '转入新增库存（新建入库层）',
};

/** 表格里"3 个子行"的样式：左边是文字标签，右边是数字（自动算出的）或输入框 */
const SUB_ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 28 };
const SUB_LABEL: React.CSSProperties = { color: '#595959', fontSize: 12, whiteSpace: 'nowrap' };
const SUB_INPUT: React.CSSProperties = { width: 64 };
const SUB_INPUT_AUTO: React.CSSProperties = { width: 64, background: '#f5f5f5' };

/** "库存单元变化"列头的 ? 提示：解释三种变化的含义 */
const UNIT_CHANGE_HELP = (
  <div style={{ lineHeight: 1.7 }}>
    <div>指每个操作目标库存单元的这些数量，操作之后在系统中处于什么状态。三种数量相加等于“指令锁定数”。</div>
    <div style={{ marginTop: 6 }}><b>不变</b>：这些数量依然使用原库存单元的编码，继续存在于系统中（由指令锁定数减去另外两项自动算出，不能手填）。</div>
    <div style={{ marginTop: 6 }}><b>转为新库存单元</b>：这些数量在原库存单元里已经不存在了，变成了另一个库存单元存在于系统中，例如异常库存检测后变成可售库存、箱库存分拣后变成散货库存。点这一行右边的“设置”选择转成哪个库存单元、转化后有多少件。</div>
    <div style={{ marginTop: 6 }}><b>销毁</b>：这些数量被销毁，不再留存在系统中。</div>
    <div style={{ marginTop: 6 }}>非标准操作指令不直接处理出库（出库涉及下架和出库的各种费用）。需要出库的库存，请保留为“不变”并转业务留存占用（放进 IOC），再追加新的出库指令。</div>
  </div>
);

/** "库存锁定处理"列头的 ? 提示：解释本指令的锁定最终怎么处理 */
const LOCK_HANDLING_HELP = (
  <div style={{ lineHeight: 1.7 }}>
    <div>库存锁定，指本指令创建时自动锁定的操作对象库存单元。这里记录指令操作完毕后，这些锁定数量最终的处理结果。三种结果相加等于“指令锁定数”。</div>
    <div style={{ marginTop: 6 }}><b>释放</b>：这些数量不再锁定，释放为可用库存。例如库存单元质检后恢复为可用库存。</div>
    <div style={{ marginTop: 6 }}><b>转业务留存占用</b>：这些数量依然锁定（不可用），留待其他指令处理。非标准操作指令不能直接出库，需要出库的库存要先转业务留存占用、放进 IOC，再追加新的出库指令，由出库指令消耗。</div>
    <div style={{ marginTop: 6 }}><b>消耗</b>：这些数量既不再锁定，也不再存在于系统中。常见的是已转化为其他库存、已销毁。</div>
    <div style={{ marginTop: 8, color: '#d9d9d9' }}>由左边的数量自动推导：转为新库存单元和销毁 = 消耗；不变 = 释放；转业务留存占用需要手动指定（从“不变”的数量里划出来）。</div>
  </div>
);

let keySeq = 0;
const newKey = () => `t${Date.now()}_${keySeq++}`;

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  return data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || err?.message || fallback;
};

const toTarget = (o: SettlementOutput): Target => ({
  key: newKey(), kind: o.kind || 'unit', iu_id: o.iu_id ?? null, project_id: o.project_id ?? null, qty: o.qty,
  age_override_at: o.age_override_at || null, age_override_reason: o.age_override_reason || '', age_manual: !!o.age_override_at,
  weight_g: o.weight_g ?? null,
});

const defaultRow = (iuId: number, locked: number): RowState => ({ keep: locked, convert: 0, destroy: 0, hold: 0, group: `u${iuId}` });
const isDefault = (row: RowState) => row.convert === 0 && row.destroy === 0 && row.hold === 0;
const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, Math.floor(Number(value) || 0)));

/**
 * 终审页的"库存结算"（库存单元层）：表里每行是本指令锁定的一个库存单元，填它 不变 / 转化 / 销毁 的数量，
 * 锁定处理（消耗 / 释放 / 转业务留存占用）由数量自动推导：转化和销毁 = 转消耗，不变的部分默认释放（可指定其中几件转业务留存占用）。
 * 没有改动过的行 = 全部不变并释放（和以前的默认行为一样）。预览确认后，在"提交终审"时由后端一次性应用。
 */
const SettlementPanel = forwardRef<SettlementPanelHandle, Props>(({ instructionId, legacy, onStatus, onChanged, reloadKey, containerCount = 0, destroyNote }, ref) => {
  const [overview, setOverview] = useState<SettlementOverview | null>(null);
  const [rows, setRows] = useState<Record<number, RowState>>({});
  const [groups, setGroups] = useState<Record<string, Target[]>>({});
  const [selected, setSelected] = useState<number[]>([]);
  const [dirty, setDirty] = useState(false);
  const [units, setUnits] = useState<Record<string, SettlementUnitInfo>>({});
  const [searchOptions, setSearchOptions] = useState<SettlementUnitInfo[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<SettlementPreview | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmNote, setConfirmNote] = useState('');
  const [editGroup, setEditGroup] = useState<string | null>(null);
  const searchSeq = useRef(0);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const settlement = overview?.settlement || null;
  const applied = settlement?.status === 'applied';
  const editable = !!overview?.can_edit;
  const pool = overview?.pool || [];
  const aiuList = overview?.aiu_options || [];
  const defaultAiuProject = aiuList[0]?.project_id ?? null;
  const lockedOf = useMemo(() => Object.fromEntries(pool.map(p => [p.iu_id, p.reserved_qty])) as Record<number, number>, [pool]);

  const apply = useCallback((data: SettlementOverview) => {
    setOverview(data);
    setUnits(prev => ({ ...prev, ...(data.units || {}) }));
    const locked: Record<number, number> = Object.fromEntries((data.pool || []).map(p => [p.iu_id, p.reserved_qty]));
    const nextRows: Record<number, RowState> = {};
    Object.entries(locked).forEach(([id, qty]) => { nextRows[Number(id)] = defaultRow(Number(id), qty); });
    const nextGroups: Record<string, Target[]> = {};
    (data.settlement?.lines || []).forEach((line, index) => {
      const key = line.inputs.length > 1 ? `g${line.id ?? index}` : `u${line.inputs[0]?.iu_id}`;
      line.inputs.forEach(input => {
        const total = locked[input.iu_id];
        if (total === undefined) return;
        const convert = clamp(input.convert_qty, 0, total);
        const destroy = clamp(input.destroy_qty, 0, total - convert);
        const keep = total - convert - destroy;
        nextRows[input.iu_id] = { keep, convert, destroy, hold: clamp(input.hold_qty, 0, keep), group: key };
      });
      if (line.outputs.length > 0) nextGroups[key] = line.outputs.map(toTarget);
    });
    setRows(nextRows);
    setGroups(nextGroups);
    setSelected([]);
    setDirty(false);
  }, []);

  const load = useCallback(async () => {
    try {
      apply(await getNonStdSettlement(instructionId));
      setError('');
    } catch (err: any) {
      setError(errorText(err, '加载库存结算失败'));
    }
  }, [instructionId, apply]);

  useEffect(() => { if (instructionId) void load(); }, [instructionId, load]);
  useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);

  const touchedRows = useMemo(() => Object.values(rows).filter(row => !isDefault(row)).length, [rows]);
  // 设置过处理的库存单元 + 已保存的容器：都需要预览确认
  const touchedCount = touchedRows + containerCount;

  // 容器草稿保存后：没有未保存的修改就整体重新加载；有就只同步版本和确认状态，不覆盖正在编辑的内容
  const refreshState = async () => {
    if (!dirty) { await load(); return; }
    try {
      const data = await getNonStdSettlement(instructionId);
      setOverview(prev => {
        if (!prev) return data;
        if (!prev.settlement || !data.settlement) return { ...prev, settlement: data.settlement };
        return { ...prev, settlement: { ...prev.settlement, version: data.settlement.version, confirmed: data.settlement.confirmed, confirmed_at: data.settlement.confirmed_at } };
      });
    } catch {
      // 同步失败不影响正在编辑的内容
    }
  };
  const firstReload = useRef(true);
  useEffect(() => {
    if (firstReload.current) { firstReload.current = false; return; }
    void refreshState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // 向外报告状态
  const statusKey = `${!overview ? 'loading' : overview.supported ? '' : 'unsupported'}|${applied}|${settlement?.confirmed}|${settlement?.confirmed_at}|${touchedCount}|${dirty}|${editable}`;
  useEffect(() => {
    if (!onStatus || !overview) return;
    const extra = {
      supported: !!overview.supported, applied, editable, confirmed: !!settlement?.confirmed, confirmedAt: settlement?.confirmed_at ?? null, hasWork: touchedCount > 0,
    };
    if (!overview.supported) onStatus({ text: '提交终审时释放锁定', color: 'default', dirty: false, needsConfirm: false, ...extra });
    else if (applied) onStatus({ text: '库存结算已应用', color: 'success', dirty: false, needsConfirm: false, ...extra });
    else if (!editable) onStatus({ text: '提交终审时释放锁定', color: 'default', dirty: false, needsConfirm: false, ...extra });
    else if (dirty) onStatus({ text: '草稿有未保存的修改', color: 'warning', dirty: true, needsConfirm: touchedCount > 0, ...extra });
    else if (touchedCount === 0) onStatus({ text: '没有设置：提交时锁定全部释放', color: 'default', dirty: false, needsConfirm: false, ...extra });
    else if (settlement?.confirmed) onStatus({ text: '结算已确认，提交终审时应用', color: 'success', dirty: false, needsConfirm: false, ...extra });
    else onStatus({ text: '结算草稿未预览确认', color: 'warning', dirty: false, needsConfirm: true, ...extra });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusKey]);

  const unitText = (id: number | null | undefined) => {
    if (!id) return '-';
    const info = units[String(id)];
    return info ? info.code : `#${id}`;
  };

  // ---------- 编辑动作 ----------
  const change = (updater: (prev: Record<number, RowState>) => Record<number, RowState>) => {
    setRows(prev => updater(prev));
    setDirty(true);
    setPreview(null);
  };

  /** 把某个库存单元从共用的组里拿出来，自成一组（它不再转化时用） */
  const detach = (iuId: number, row: RowState): RowState => (row.group === `u${iuId}` ? row : { ...row, group: `u${iuId}` });

  const setConvert = (iuId: number, value: number) => change(prev => {
    const total = lockedOf[iuId] || 0;
    const row = prev[iuId];
    const convert = clamp(value, 0, total - row.destroy);
    const keep = total - convert - row.destroy;
    const next = { ...row, convert, keep, hold: Math.min(row.hold, keep) };
    return { ...prev, [iuId]: convert === 0 ? detach(iuId, next) : next };
  });
  const setDestroy = (iuId: number, value: number) => change(prev => {
    const total = lockedOf[iuId] || 0;
    const row = prev[iuId];
    const destroy = clamp(value, 0, total - row.convert);
    const keep = total - row.convert - destroy;
    return { ...prev, [iuId]: { ...row, destroy, keep, hold: Math.min(row.hold, keep) } };
  });
  const setHold = (iuId: number, value: number) => change(prev => ({ ...prev, [iuId]: { ...prev[iuId], hold: clamp(value, 0, prev[iuId].keep) } }));

  /** 批量：对选中的库存单元一起设置 */
  const batch = (kind: 'release' | 'hold' | 'destroy' | 'convert') => {
    if (selected.length === 0) return;
    const groupKey = `g${newKey()}`;
    change(prev => {
      const next = { ...prev };
      selected.forEach(iuId => {
        const total = lockedOf[iuId] || 0;
        if (kind === 'release') next[iuId] = defaultRow(iuId, total);
        else if (kind === 'hold') next[iuId] = { keep: total, convert: 0, destroy: 0, hold: total, group: `u${iuId}` };
        else if (kind === 'destroy') next[iuId] = { keep: 0, convert: 0, destroy: total, hold: 0, group: `u${iuId}` };
        else next[iuId] = { keep: 0, convert: total, destroy: 0, hold: 0, group: groupKey };
      });
      return next;
    });
    if (kind === 'convert') {
      setGroups(prev => ({ ...prev, [groupKey]: [] }));
      setEditGroup(groupKey);
    }
  };

  const updateTargets = (key: string, updater: (list: Target[]) => Target[]) => {
    setGroups(prev => ({ ...prev, [key]: updater(prev[key] || []) }));
    setDirty(true);
    setPreview(null);
  };
  const groupMembers = (key: string) => Object.entries(rows).filter(([, row]) => row.group === key && row.convert > 0).map(([id]) => Number(id));
  const groupConvertTotal = (key: string) => groupMembers(key).reduce((total, id) => total + rows[id].convert, 0);

  /** 转化目标的模糊搜索：按系统编码、产品名称、产品编码、分拣条码，边输入边搜（停顿 0.25 秒后请求，只认最后一次的结果） */
  const onSearch = (text: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const seq = ++searchSeq.current;
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const list = await searchNonStdSettlementUnits(instructionId, text);
        if (seq !== searchSeq.current) return;
        setSearchOptions(list);
        setUnits(prev => ({ ...prev, ...Object.fromEntries(list.map(u => [String(u.id), u])) }));
      } catch { /* 搜索失败不影响已选内容 */ } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 250);
  };
  /** 下拉里的一个库存单元：系统编码 + 内容项（产品名(编码:自定义编码 条码:分拣条码 等N个)×数量，多个内容项依次排开）；选中后筛选框里也是同样的样式 */
  const unitOptionLabel = (info: SettlementUnitInfo) => (
    <span className="nonstd-settlement-option">
      <b>{info.code}</b>
      {/* 本指令涉及多个项目时，要看得出这个库存单元属于哪个项目 */}
      {aiuList.length > 1 && info.project_code ? <span className="nonstd-settlement-option-project" style={{ marginLeft: 4, color: '#1677ff' }}>[{info.project_code}]</span> : null}
      <span style={{ marginLeft: 8, color: '#8c8c8c' }}>内容项:</span>
      {(info.content_items || []).length === 0
        ? <span style={{ marginLeft: 4, color: '#bfbfbf' }}>无</span>
        : (info.content_items || []).map((it, i) => <span key={i} style={{ marginLeft: 8 }}>{optionContentLine(it)}</span>)}
    </span>
  );

  /** 下拉的候选 = 最近一次搜索的结果 + 已经选中的库存单元（保证选中后能显示出来）；AIU 在转化目标里单独选，不出现在这里 */
  const unitOptions = (selectedIds: number[] = []) => {
    const seen = new Set<number>();
    const list: SettlementUnitInfo[] = [];
    [...searchOptions, ...selectedIds.map(id => units[String(id)]).filter(Boolean)].forEach(info => {
      if (info && info.unit_type !== 'AIU' && !seen.has(info.id)) { seen.add(info.id); list.push(info); }
    });
    return list.map(info => ({ value: info.id, label: unitOptionLabel(info) }));
  };

  // ---------- 保存 / 预览 / 确认 ----------
  /** 把表里设置过的库存单元整理成要保存的处理组；没改动的行不保存（默认全部释放） */
  const buildLines = (): SettlementLine[] => {
    const byGroup: Record<string, number[]> = {};
    Object.entries(rows).forEach(([id, row]) => {
      if (isDefault(row)) return;
      (byGroup[row.group] = byGroup[row.group] || []).push(Number(id));
    });
    return Object.entries(byGroup).map(([key, ids]) => {
      const hasConvert = ids.some(id => rows[id].convert > 0);
      return {
        note: '',
        inputs: ids.map(id => ({ iu_id: id, keep_qty: rows[id].keep, convert_qty: rows[id].convert, destroy_qty: rows[id].destroy, hold_qty: rows[id].hold })),
        outputs: hasConvert ? (groups[key] || []).map(t => ({
          kind: t.kind, iu_id: t.kind === 'unit' ? t.iu_id : null,
          project_id: t.kind === 'aiu' ? (t.project_id ?? defaultAiuProject) : null, qty: Number(t.qty),
          age_override_at: t.age_manual ? (t.age_override_at || null) : null,
          age_override_reason: t.age_manual ? (t.age_override_reason || '') : '',
          weight_g: t.kind === 'aiu' ? Number(t.weight_g) : null,
        })) : [],
      };
    });
  };

  /** 转化了但没设置完整的转化目标 */
  const problems = useMemo(() => {
    const list: string[] = [];
    const seen = new Set<string>();
    Object.values(rows).forEach(row => {
      if (row.convert <= 0 || seen.has(row.group)) return;
      seen.add(row.group);
      const targets = groups[row.group] || [];
      if (targets.length === 0) list.push('有库存单元填了转化数量，但还没有设置转化目标');
      else if (targets.some(t => (t.kind === 'unit' && !t.iu_id) || !(Number(t.qty) >= 1))) list.push('转化目标需要选择库存单元并填写大于 0 的数量');
      else if (targets.some(t => t.kind === 'aiu' && !(Number(t.weight_g) >= 1))) list.push('转入项目异常库存(AIU)需要填写这批 AIU 的总重量（g）');
      else {
        const keys = targets.map(t => (t.kind === 'aiu' ? `aiu:${t.project_id ?? defaultAiuProject}` : `unit:${t.iu_id}`));
        if (new Set(keys).size !== keys.length) list.push('同一个转化目标只能设置一行');
      }
    });
    return Array.from(new Set(list));
  }, [rows, groups, defaultAiuProject]);

  const save = async (silent = false): Promise<boolean> => {
    if (problems.length > 0) { message.warning(problems[0]); return false; }
    setBusy(true);
    try {
      const data = await saveNonStdSettlement(instructionId, { version: settlement?.version ?? 0, lines: buildLines() });
      apply(data);
      if (!silent) message.success('结算草稿已保存');
      onChanged?.();
      return true;
    } catch (err: any) {
      if (err?.response?.status === 409) {
        Modal.warning({ title: '草稿已被别人修改', content: '已重新加载最新的结算草稿，请检查后再改。' });
        await load();
      } else {
        message.error(errorText(err, '保存失败'));
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async () => {
    if (dirty && !(await save(true))) return;
    setBusy(true);
    try {
      const result = await previewNonStdSettlement(instructionId);
      setPreview(result);
      setAcknowledged(false);
      setConfirmNote('');
      setPreviewOpen(true);
    } catch (err: any) {
      message.error(errorText(err, '预览失败'));
    } finally {
      setBusy(false);
    }
  };

  const doConfirm = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const data = await confirmNonStdSettlement(instructionId, {
        fingerprint: preview.fingerprint, container_fingerprint: preview.container_fingerprint || '', acknowledged, note: confirmNote,
      });
      apply(data);
      setPreviewOpen(false);
      message.success('终审操作已确认：提交终审时会按预览的结果应用');
      onChanged?.();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        message.warning('库存或草稿在预览后发生了变化，请重新预览');
        setPreviewOpen(false);
      } else {
        message.error(errorText(err, '确认失败'));
      }
    } finally {
      setBusy(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: async () => (dirty ? save(true) : true),
    openPreview,
    refresh: refreshState,
  }));

  if (error && !overview) {
    return (
      <>
        <Alert type="error" showIcon style={{ marginBottom: 10 }} message={error} />
        {legacy}
      </>
    );
  }
  if (!overview) return <div style={{ textAlign: 'center', padding: 16 }}><Spin /></div>;
  if (!overview.supported) {
    return (
      <>
        <Alert type="info" showIcon style={{ marginBottom: 10 }} message={overview.unsupported_reason || '这个指令不支持库存结算'} />
        {legacy}
      </>
    );
  }

  // ---------- 展示用的小零件 ----------
  const targetText = (targets: Array<{ kind: string; iu_id: number | null; qty: number }>) => (
    targets.map(t => `${t.kind === 'aiu' ? '项目AIU' : unitText(t.iu_id)} × ${t.qty}`).join('；')
  );

  const actionRows = (list: SettlementActionRow[]) => (
    <Table
      size="small"
      rowKey={(_, index) => String(index)}
      pagination={false}
      dataSource={list}
      columns={[
        { title: '组', dataIndex: 'line', width: 56, render: (v: number | null) => (v ? `第${v}组` : '默认') },
        {
          title: '动作',
          dataIndex: 'type',
          width: 230,
          render: (v: string, row) => `${ACTION_ROW_TEXT[v] || v}${row.why === 'leftover' ? '（没设置的锁定）' : row.why === 'keep_hold' ? '（不变转留存：先释放原锁定）' : row.why === 'keep' && v === 'reserve_hold' ? '（不变转留存）' : ''}`,
        },
        {
          title: '库存单元',
          dataIndex: 'iu_code',
          render: (v: string, row) => (
            <div>
              <div>{v || unitText(row.iu_id)}</div>
              {row.weight_g ? <div style={{ fontSize: 12, color: '#8c8c8c' }}>总重量 {row.weight_g} g，AIU 平均重量 {row.avg_weight_before ?? 0} → {row.avg_weight_after} g/件</div> : null}
            </div>
          ),
        },
        { title: '数量', dataIndex: 'qty', width: 80 },
        {
          title: '库龄（入库层时间）',
          width: 210,
          render: (_: any, row) => (row.type === 'produce' && row.received_at
            ? `${dayjs(row.received_at).format('YYYY-MM-DD HH:mm')}（${row.age_source === 'override' ? '手动指定' : '按数量加权'}）`
            : '-'),
        },
      ]}
    />
  );

  /** 预览"三、最终的容器"：这次预览用的容器草稿，每个容器一行（编码在提交终审时才生成） */
  const finalContainersTable = (list: ContainerDraft[], ledger: ContainerLedgerRow[]) => {
    const scCount = list.filter(c => c.kind === 'sc').length;
    // 内容里的库存单元编码从同一次预览的对账表里取；还不存在的项目 AIU 是 aiu:<项目 id>
    const unitOf = (item: { iu_id?: number | null; aiu_project_id?: number | null }) => {
      const row = item.iu_id
        ? ledger.find(r => r.unit_key === String(item.iu_id))
        : (ledger.find(r => r.unit_key === `aiu:${item.aiu_project_id}`) || ledger.find(r => r.unit_type === 'AIU' && r.project_id === item.aiu_project_id));
      if (!row) return item.iu_id ? `#${item.iu_id}` : '项目 AIU（将创建）';
      return row.new_unit ? `${row.iu_code}（将创建）` : row.iu_code;
    };
    const num = (v: number | string | null | undefined) => (v === null || v === undefined || v === '' ? '-' : Number(v));
    const locationCell = (c: ContainerDraft) => {
      const text = (c.location_text || '').trim();
      if (c.location_match === 'formal') return <span>{c.location_code || text}<Tag color="green" style={{ marginLeft: 6 }}>正式仓位</Tag></span>;
      if (!text) return '-';
      return <span>{text}<Tag color="orange" style={{ marginLeft: 6 }}>{c.kind === 'sc' ? '临时仓位：将记为未上架' : '临时仓位'}</Tag></span>;
    };
    return (
      <>
        <div className="nonstd-settlement-preview-title" style={{ fontWeight: 700, fontSize: 15, margin: '18px 0 4px' }}>三、最终的容器</div>
        <div className="nonstd-settlement-preview-boxes-note" style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>
          共 {list.length} 个容器（SC {scCount} 个、IOC {list.length - scCount} 个）；容器编码在提交终审时生成。
        </div>
        <Table
          className="nonstd-settlement-preview-boxes"
          size="small"
          rowKey={(c, index) => String(c.id ?? index)}
          pagination={false}
          dataSource={list}
          columns={[
            { title: '序号', width: 60, render: (_: any, __: ContainerDraft, index: number) => index + 1 },
            { title: '类型', width: 140, render: (_: any, c: ContainerDraft) => <Tag color={c.kind === 'ioc' ? 'red' : 'blue'}>{c.kind === 'ioc' ? 'IOC 指令产出容器' : 'SC 仓储容器'}</Tag> },
            { title: '容器类型', width: 90, render: (_: any, c: ContainerDraft) => containerTypeText(c.container_type) },
            { title: '仓位', width: 150, render: (_: any, c: ContainerDraft) => locationCell(c) },
            {
              title: '尺寸/重量',
              width: 130,
              render: (_: any, c: ContainerDraft) => (c.kind === 'ioc'
                ? <div className="nonstd-settlement-preview-box-size"><div>{[c.length_cm, c.width_cm, c.height_cm].map(num).join('×')} cm</div><div>{num(c.weight_g)} g</div></div>
                : '-'),
            },
            {
              title: '内容',
              width: 200,
              render: (_: any, c: ContainerDraft) => ((c.items || []).length === 0
                ? '-'
                : <div className="nonstd-settlement-preview-box-items">{(c.items || []).map((item, i) => <div key={i}>{unitOf(item)} × {item.qty}</div>)}</div>),
            },
            {
              title: '备注',
              width: 240,
              render: (_: any, c: ContainerDraft) => {
                // 只显示前 10 个字，超出用…代替，鼠标移上去显示完整备注
                const text = (c.remarks || '').trim();
                if (!text) return '-';
                const chars = Array.from(text);
                if (chars.length <= 10) return text;
                return (
                  <Tooltip title={text} overlayStyle={{ maxWidth: 420 }}>
                    <span className="nonstd-settlement-preview-box-remark" style={{ cursor: 'default' }}>{chars.slice(0, 10).join('')}…</span>
                  </Tooltip>
                );
              },
            },
          ]}
        />
      </>
    );
  };

  const previewBody = preview ? (
    <div>
      {preview.empty ? <Alert type="info" showIcon message={preview.message || '没有设置任何处理'} /> : (
        <>
          {preview.blockers.map((b, i) => <Alert key={`b${i}`} type="error" showIcon style={{ marginBottom: 6 }} message={`阻断：${b.message}`} />)}
          {preview.warnings.map((w, i) => <Alert key={`w${i}`} type="warning" showIcon style={{ marginBottom: 6 }} message={`警告：${w.message}`} />)}
          {(preview.notices || []).map((n, i) => <Alert key={`n${i}`} type="info" showIcon style={{ marginBottom: 6 }} message={`提示：${n.message}`} />)}
          {preview.source_totals ? (
            <div className="nonstd-settlement-preview-summary">
              <div className="nonstd-settlement-preview-section" style={{ fontWeight: 700, fontSize: 15, margin: '12px 0 4px' }}>一、库存结算</div>
              <div className="nonstd-settlement-preview-title" style={{ fontWeight: 600, margin: '10px 0 4px' }}>
                指令操作对象汇总：共 {preview.source_totals.units} 个库存单元，指令锁定数合计 {preview.source_totals.locked} 件
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>只统计本指令锁定的那部分库存，不含这些库存单元的其他在库库存。</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div className="nonstd-settlement-preview-change" style={{ flex: '1 1 300px', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 12px' }}>
                  <div style={{ color: '#595959', marginBottom: 4 }}>库存单元变化</div>
                  <Space size={24} wrap>
                    <Statistic title="不变" value={preview.source_totals.keep} suffix="件" valueStyle={{ fontSize: 20 }} />
                    <Statistic title="转为新库存单元" value={preview.source_totals.convert} suffix="件" valueStyle={{ fontSize: 20 }} />
                    <Statistic title="销毁" value={preview.source_totals.destroy} suffix="件" valueStyle={{ fontSize: 20 }} />
                  </Space>
                </div>
                <div className="nonstd-settlement-preview-lock" style={{ flex: '1 1 300px', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 12px' }}>
                  <div style={{ color: '#595959', marginBottom: 4 }}>库存锁定处理</div>
                  <Space size={24} wrap>
                    <Statistic title="释放" value={preview.source_totals.release} suffix="件" valueStyle={{ fontSize: 20 }} />
                    <Statistic title="转业务留存占用" value={preview.source_totals.hold} suffix="件" valueStyle={{ fontSize: 20 }} />
                    <Statistic title="消耗" value={preview.source_totals.consume} suffix="件" valueStyle={{ fontSize: 20 }} />
                  </Space>
                </div>
              </div>
            </div>
          ) : null}
          {preview.units.some(u => u.is_target) ? (
            <>
              <div className="nonstd-settlement-preview-title" style={{ fontWeight: 600, margin: '14px 0 4px' }}>转化为的库存单元（新增）</div>
              <Table
                className="nonstd-settlement-preview-target"
                size="small"
                rowKey={(row) => `t${row.iu_id ?? row.iu_code}`}
                pagination={false}
                dataSource={preview.units.filter(u => u.is_target)}
                columns={[
                  {
                    title: '库存单元',
                    dataIndex: 'iu_code',
                    width: 190,
                    render: (v: string, row) => (
                      <span>
                        <b>{v}</b>
                        {row.new_unit ? <Tag color="blue" style={{ marginLeft: 6 }}>将创建</Tag> : null}
                      </span>
                    ),
                  },
                  {
                    title: '项目',
                    width: 90,
                    render: (_: any, row) => <span className="nonstd-settlement-preview-project">{row.project_code || '-'}</span>,
                  },
                  {
                    title: '库存单元内容项',
                    render: (_: any, row) => (
                      (row.content_items || []).length === 0
                        ? <span style={{ color: '#bfbfbf' }}>无</span>
                        : <div style={{ fontSize: 12 }}>{(row.content_items || []).map((it, i) => <div key={i} className="nonstd-settlement-preview-content">{optionContentLine(it)}</div>)}</div>
                    ),
                  },
                  {
                    title: '新增数量',
                    width: 190,
                    render: (_: any, row) => (
                      <div>
                        <b className="nonstd-settlement-preview-added" style={{ color: '#389e0d' }}>+{row.produced.reduce((total, p) => total + p.qty, 0)}</b>
                        {row.produced.filter(p => p.weight_g).map((p, i) => (
                          <div key={i} style={{ fontSize: 12, color: '#8c8c8c' }}>总重量 {p.weight_g} g，AIU 平均重量 {p.avg_weight_before ?? 0} → {p.avg_weight_after} g/件</div>
                        ))}
                      </div>
                    ),
                  },
                  {
                    title: '库龄',
                    width: 230,
                    render: (_: any, row) => (
                      <div style={{ fontSize: 12 }}>
                        {row.produced.map((p, i) => (
                          <div key={i} className="nonstd-settlement-preview-age">
                            {row.produced.length > 1 ? `${p.qty} 件：` : ''}
                            {/* 鼠标移上去显示 UTC 时间、用户时区和本地时间（和系统里其他页面的时间一致） */}
                            <TimezoneAwareTime time={p.received_at} format="YYYY-MM-DD HH:mm" showTimezone={false} />
                            （{p.age_source === 'override' ? '手动指定' : '按数量加权'}）
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            </>
          ) : null}
          {preview.containers?.engaged ? (
            <>
              <div className="nonstd-settlement-preview-title" style={{ fontWeight: 700, fontSize: 15, margin: '18px 0 4px' }}>二、终审容器分配</div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>每个库存单元需要分配到新容器的数量和已分配的数量；IOC 里的数量要等于转业务留存占用。</div>
              <Table
                className="nonstd-settlement-preview-containers"
                size="small"
                rowKey="unit_key"
                pagination={false}
                dataSource={preview.containers.ledger}
                columns={[
                  { title: '库存单元', dataIndex: 'iu_code', render: (v: string, row) => <span>{v}{row.new_unit ? <Tag color="blue" style={{ marginLeft: 6 }}>将创建</Tag> : null}</span> },
                  { title: '需分配数量', dataIndex: 'total', width: 100 },
                  { title: '转业务留存', dataIndex: 'hold', width: 100 },
                  { title: '已分配 SC', dataIndex: 'sc_qty', width: 90 },
                  { title: '已分配 IOC', dataIndex: 'ioc_qty', width: 100 },
                  {
                    title: '未分配',
                    dataIndex: 'unassigned',
                    width: 90,
                    render: (v: number) => <span style={v !== 0 ? { color: '#cf1322', fontWeight: 600 } : { color: '#389e0d' }}>{v < 0 ? `多分 ${-v}` : v}</span>,
                  },
                ]}
              />
            </>
          ) : null}
          {(preview.containers?.list || []).length > 0 ? finalContainersTable(preview.containers?.list || [], preview.containers?.ledger || []) : null}
        </>
      )}
    </div>
  ) : null;

  /** 已保存（或已应用）的处理：只读表 */
  const readonlyTable = () => {
    // 只读的子行：名称后面紧跟数字（不像编辑表那样把数字推到右边）
    const RO_ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, minHeight: 28 };
    const flat: Array<{ key: string; iu_id: number; keep: number; convert: number; destroy: number; hold: number; targets: SettlementOutput[] }> = [];
    (settlement?.lines || []).forEach((line, index) => {
      line.inputs.forEach(input => flat.push({
        key: `${index}-${input.iu_id}`, iu_id: input.iu_id, keep: input.keep_qty, convert: input.convert_qty, destroy: input.destroy_qty,
        hold: input.hold_qty, targets: input.convert_qty > 0 ? line.outputs : [],
      }));
    });
    return (
      <Table
        size="small"
        rowKey="key"
        pagination={false}
        dataSource={flat}
        columns={[
          { title: '库存单元', width: 240, render: (_: any, row) => unitText(row.iu_id) },
          {
            title: '库存单元转化',
            width: 150,
            render: (_: any, row) => (
              <div className="nonstd-settlement-readonly-change">
                <div style={RO_ROW}><span style={SUB_LABEL}>不变</span><span>{row.keep}</span></div>
                <div style={RO_ROW}><span style={SUB_LABEL}>转为新库存单元</span><span>{row.convert}</span></div>
                <div style={RO_ROW}><span style={SUB_LABEL}>销毁</span><span>{row.destroy}</span></div>
              </div>
            ),
          },
          {
            title: '锁定处理',
            width: 150,
            render: (_: any, row) => (
              <div className="nonstd-settlement-readonly-lock">
                <div style={RO_ROW}><span style={SUB_LABEL}>释放</span><span>{row.keep - row.hold}</span></div>
                <div style={RO_ROW}><span style={SUB_LABEL}>转业务留存占用</span><span>{row.hold}</span></div>
                <div style={RO_ROW}><span style={SUB_LABEL}>消耗</span><span>{row.convert + row.destroy}</span></div>
              </div>
            ),
          },
          {
            title: '转化目标',
            width: 240,
            render: (_: any, row) => (row.targets.length
              ? <div className="nonstd-settlement-readonly-targets">{row.targets.map((t, i) => <div key={i}>{targetText([t])}</div>)}</div>
              : '-'),
          },
        ]}
      />
    );
  };

  // ---------- 已应用：只读展示 ----------
  if (applied && settlement) {
    const result = settlement.apply_result || {};
    return (
      <div>
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 10 }}
          message={`库存结算已在终审提交时应用${settlement.applied_at ? `（${dayjs(settlement.applied_at).format('YYYY-MM-DD HH:mm')}）` : ''}`}
          description={`转消耗 ${result.summary?.consume_qty || 0} 件，转入新增 ${result.summary?.produce_qty || 0} 件，释放锁定 ${result.summary?.release_qty || 0} 件，业务留存占用 ${result.summary?.hold_qty || 0} 件。`}
        />
        {readonlyTable()}
        <div style={{ fontWeight: 600, margin: '10px 0 4px' }}>实际执行的动作</div>
        {actionRows(result.actions || [])}
      </div>
    );
  }

  // ---------- 不能编辑（不是待终审 / 不是管理员）：只读 ----------
  if (!editable) {
    return (
      <div>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 10 }}
          message={overview.instruction_status === 'operation_done' ? '只有管理员可以编辑库存结算。' : '指令不在“待终审”状态，不能编辑库存结算；提交终审时锁定按默认策略释放。'}
        />
        {(settlement?.lines || []).length > 0 ? readonlyTable() : legacy}
      </div>
    );
  }

  // ---------- 编辑：操作目标处理表 ----------
  const poolRows = pool.map(p => ({ ...p, row: rows[p.iu_id] || defaultRow(p.iu_id, p.reserved_qty) }));

  /** 复制库存单元的系统编码 */
  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };

  /** 内容物：只显示第一种，还有别的就用…代替，鼠标移上去显示全部（带分拣条码） */
  const contentsCell = (items?: Array<{ name: string; qty: number; barcodes?: string[] }>) => {
    if (!items || items.length === 0) return <span style={{ color: '#bfbfbf' }}>无内容物信息</span>;
    const first = `${items[0].name} × ${items[0].qty}`;
    return (
      <Tooltip title={<div>{items.map((it, i) => <div key={i}>{contentLine(it)}</div>)}</div>} placement="topLeft">
        <span className="nonstd-settlement-contents" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: '#595959' }}>
          {first}{items.length > 1 ? '…' : ''}
        </span>
      </Tooltip>
    );
  };

  const targetCell = (iuId: number, row: RowState) => {
    if (row.convert <= 0) return '-';
    const targets = groups[row.group] || [];
    const members = groupMembers(row.group);
    return (
      <Space size={6} wrap>
        <span>{targets.length > 0 ? `→ ${targetText(targets)}` : <span style={{ color: '#cf1322' }}>未设置</span>}</span>
        {members.length > 1 ? <Tag style={{ marginInlineEnd: 0 }}>与另外 {members.length - 1} 个一起转化</Tag> : null}
        <a onClick={() => setEditGroup(row.group)}>{targets.length > 0 ? '修改' : '设置'}</a>
      </Space>
    );
  };

  const targetModal = (() => {
    if (!editGroup) return null;
    const key = editGroup;
    const targets = groups[key] || [];
    const members = groupMembers(key);
    const sourceIds = new Set(members);
    const convertTotal = groupConvertTotal(key);
    const outTotal = targets.reduce((total, t) => total + (Number(t.qty) || 0), 0);
    const aiuProjectOf = (t: Target) => t.project_id ?? defaultAiuProject;
    /** 其他行已经选了的库存单元 / 项目 AIU：这一行里不能再选 */
    const usedUnits = (except: number) => new Set(targets.filter((x, i) => i !== except && x.kind === 'unit' && x.iu_id).map(x => x.iu_id as number));
    const usedAiuProjects = (except: number) => new Set(targets.filter((x, i) => i !== except && x.kind === 'aiu').map(x => aiuProjectOf(x)));
    const firstFreeAiuProject = (except: number) => {
      const used = usedAiuProjects(except);
      return aiuList.find(o => !used.has(o.project_id))?.project_id ?? null;
    };
    const patchTarget = (index: number, patch: Partial<Target>) => updateTargets(key, list => list.map((t, i) => (i === index ? { ...t, ...patch } : t)));
    return (
      <Modal
        title="设置转化目标"
        open
        width={1240}
        style={{ maxWidth: 'calc(100vw - 32px)' }}
        onCancel={() => setEditGroup(null)}
        destroyOnClose
        footer={[<Button key="ok" type="primary" onClick={() => setEditGroup(null)}>完成</Button>]}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 10 }}
          message="只能转为项目基础配置中已经存在的库存单元，或项目的异常库存单元（AIU）。如果要转为不存在的库存单元，需要先修改项目基础配置，创建这个库存单元。"
        />
        <div style={{ marginBottom: 8 }}>
          转化来源：{members.length === 0 ? '（没有库存单元转化到这里）' : members.map(id => `${unitText(id)} × ${rows[id].convert}`).join('；')}
          <span style={{ marginLeft: 12, color: '#8c8c8c' }}>转化前合计 {convertTotal} 件 → 转化后合计 {outTotal} 件（转化后的数量由你填写，可以比转化前多，例如 1 箱拆成 24 件）</span>
        </div>
        {targets.map((t, index) => {
          const aiuOption = aiuList.find(o => o.project_id === aiuProjectOf(t));
          const takenUnits = usedUnits(index);
          const aiuAllTaken = aiuList.length > 0 && aiuList.every(o => usedAiuProjects(index).has(o.project_id));
          return (
            <div key={t.key} className="nonstd-settlement-target" style={{ marginBottom: 8 }}>
              <Space wrap>
                <Select
                  style={{ width: 170 }}
                  value={t.kind}
                  options={[
                    { value: 'unit', label: '项目正常库存(非AIU)' },
                    { value: 'aiu', label: '项目异常库存(AIU)', disabled: t.kind !== 'aiu' && aiuAllTaken },
                  ]}
                  onChange={v => patchTarget(index, v === 'aiu' ? { kind: 'aiu', iu_id: null, project_id: firstFreeAiuProject(index) } : { kind: 'unit', project_id: null })}
                />
                {t.kind === 'unit' ? (
                  <Select
                    style={{ width: 540 }}
                    popupMatchSelectWidth={false}
                    dropdownStyle={{ minWidth: 800 }}
                    showSearch
                    filterOption={false}
                    loading={searching}
                    placeholder="输入系统编码、产品名称、产品编码或分拣条码搜索项目基础配置里的库存单元"
                    notFoundContent={searching ? '搜索中…' : '没有匹配的库存单元（只能选项目基础配置里已有的）'}
                    value={t.iu_id ?? undefined}
                    options={unitOptions(targets.map(x => x.iu_id).filter(Boolean) as number[]).map(o => ({ ...o, disabled: takenUnits.has(o.value) || sourceIds.has(o.value) }))}
                    onSearch={onSearch}
                    onFocus={() => onSearch('')}
                    onChange={v => patchTarget(index, { iu_id: v })}
                  />
                ) : (
                  <span className="nonstd-settlement-aiu-cell" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: 540 }}>
                    {aiuList.length > 1 ? (
                      <Select
                        style={{ width: 130, flex: 'none' }}
                        value={aiuProjectOf(t) ?? undefined}
                        options={aiuList.map(o => ({ value: o.project_id, label: o.project_code || `项目 ${o.project_id}`, disabled: o.project_id !== aiuProjectOf(t) && usedAiuProjects(index).has(o.project_id) }))}
                        onChange={v => patchTarget(index, { project_id: v })}
                      />
                    ) : null}
                    {aiuOption?.aiu ? (
                      <span className="nonstd-settlement-aiu-info" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <b>{aiuOption.aiu.code}</b>
                        <span style={{ marginLeft: 6, color: '#8c8c8c' }}>
                          （{[aiuOption.aiu.unit_type, aiuOption.aiu.project_code, aiuOption.aiu.sorting_group].filter(Boolean).join(' · ')}）
                        </span>
                      </span>
                    ) : <span className="nonstd-settlement-aiu-info" style={{ flex: 1, color: '#d46b08' }}>项目当前无AIU，将新建</span>}
                  </span>
                )}
                <InputNumber min={1} precision={0} value={t.qty} onChange={v => patchTarget(index, { qty: Number(v) || 1 })} />
                {t.kind === 'aiu' ? (
                  <Tooltip title="这批转入 AIU 的总重量（克，必填）：用来按数量加权更新 AIU 的平均重量">
                    <InputNumber
                      className="nonstd-settlement-weight"
                      style={{ width: 150 }}
                      min={1}
                      precision={0}
                      placeholder="总重量（必填）"
                      addonAfter="g"
                      status={Number(t.weight_g) >= 1 ? undefined : 'error'}
                      value={t.weight_g ?? undefined}
                      onChange={v => patchTarget(index, { weight_g: v === null || v === undefined ? null : Number(v) })}
                    />
                  </Tooltip>
                ) : <span style={{ display: 'inline-block', width: 150 }} />}
                <Tooltip title="默认按被消耗的各入库层数量加权；需要时可以手动指定，必须写原因">
                  <span>
                    <Switch
                      size="small"
                      checked={!!t.age_manual}
                      onChange={v => patchTarget(index, { age_manual: v, age_override_at: v ? (t.age_override_at || dayjs().toISOString()) : null })}
                    />
                    {' '}手动库龄
                  </span>
                </Tooltip>
                <a onClick={() => updateTargets(key, list => list.filter((_, i) => i !== index))}><DeleteOutlined /> 移除</a>
              </Space>
              {t.age_manual ? (
                <Space wrap style={{ marginTop: 4, marginLeft: 8 }}>
                  <DatePicker showTime value={t.age_override_at ? dayjs(t.age_override_at) : null} onChange={v => patchTarget(index, { age_override_at: v ? v.toISOString() : null })} />
                  <Input
                    style={{ width: 260 }}
                    placeholder="手动指定库龄的原因（必填）"
                    maxLength={200}
                    value={t.age_override_reason}
                    onChange={e => patchTarget(index, { age_override_reason: e.target.value })}
                  />
                </Space>
              ) : null}
            </div>
          );
        })}
        <Button size="small" icon={<PlusOutlined />} onClick={() => updateTargets(key, list => [...list, { key: newKey(), kind: 'unit', iu_id: null, qty: Math.max(1, convertTotal), age_manual: false }])}>添加目标库存单元</Button>
      </Modal>
    );
  })();

  return (
    <div>
      {destroyNote ? (
        <Alert
          className="nonstd-settlement-destroy-note"
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message={`执行阶段记录了销毁：${destroyNote}。这些记录不会自动扣库存，请对照在下面填写“销毁”；记录的是实物件数，这里按库存单元数量填，两者不一定相同（例如 1 箱 24 件，这里填 1）。`}
        />
      ) : null}
      <Space wrap style={{ marginBottom: 8 }}>
        <Button disabled={selected.length === 0} onClick={() => batch('release')}>不变·释放</Button>
        <Button disabled={selected.length === 0} onClick={() => batch('hold')}>不变·转业务留存占用</Button>
        <Button disabled={selected.length === 0} onClick={() => batch('destroy')}>全部销毁库存</Button>
        <Button disabled={selected.length === 0} onClick={() => batch('convert')}>转化为…（选中的合并转化）</Button>
        <span style={{ color: '#8c8c8c' }}>{selected.length > 0 ? `已选 ${selected.length} 个库存单元` : '勾选多行可以批量设置'}</span>
      </Space>
      <Table
        size="small"
        rowKey="iu_id"
        pagination={false}
        dataSource={poolRows}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="本指令没有有效的库存锁定" /> }}
        scroll={{ x: 740 }}
        rowSelection={{ selectedRowKeys: selected, onChange: keys => setSelected(keys as number[]), fixed: true }}
        columns={[
          {
            title: '操作目标（库存单元）',
            width: 170,
            fixed: 'left',
            render: (_: any, item) => {
              const code = item.code || unitText(item.iu_id);
              return (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Tooltip title={[item.unit_type, item.project_code, item.sorting_group].filter(Boolean).join(' · ') || undefined}>
                      <b className="nonstd-settlement-code">{code}</b>
                    </Tooltip>
                    <Button size="small" type="text" aria-label="复制库存单元编码" className="nonstd-settlement-copy" icon={<CopyOutlined />} onClick={() => { void copyCode(code); }} />
                  </div>
                  {contentsCell(item.content_items)}
                  <div style={{ fontSize: 12, marginTop: 2 }}>指令锁定数 <b>{item.reserved_qty}</b></div>
                  {/* 拣货记录里拣出的数量（理论库存和实际库存可能有误差，和锁定数不一致是正常的，只做参考） */}
                  <div className="nonstd-settlement-picked" style={{ fontSize: 12, marginTop: 2, color: '#595959' }}>
                    拣出数量 <b>{item.picked_qty == null ? '-' : item.picked_qty}</b>
                  </div>
                </div>
              );
            },
          },
          {
            title: (
              <span>
                库存单元变化
                <Tooltip title={UNIT_CHANGE_HELP} placement="bottom" overlayStyle={{ maxWidth: 420 }}>
                  <QuestionCircleOutlined className="nonstd-settlement-change-help" style={{ marginLeft: 4, color: '#8c8c8c', cursor: 'help' }} />
                </Tooltip>
              </span>
            ),
            width: 180,
            render: (_: any, item) => (
              <div className="nonstd-settlement-block">
                <div style={SUB_ROW}>
                  <span style={SUB_LABEL}>不变</span>
                  <InputNumber size="small" readOnly controls={false} style={SUB_INPUT_AUTO} className="nonstd-settlement-keep" value={item.row.keep} />
                </div>
                <div style={SUB_ROW}>
                  <span style={SUB_LABEL}>转为新库存单元</span>
                  <InputNumber size="small" style={SUB_INPUT} className="nonstd-settlement-convert" min={0} max={item.reserved_qty - item.row.destroy} precision={0} value={item.row.convert} onChange={v => setConvert(item.iu_id, Number(v) || 0)} />
                </div>
                <div style={SUB_ROW}>
                  <span style={SUB_LABEL}>销毁</span>
                  <InputNumber size="small" style={SUB_INPUT} className="nonstd-settlement-destroy" min={0} max={item.reserved_qty - item.row.convert} precision={0} value={item.row.destroy} onChange={v => setDestroy(item.iu_id, Number(v) || 0)} />
                </div>
              </div>
            ),
          },
          { title: '转化目标', width: 170, render: (_: any, item) => targetCell(item.iu_id, item.row) },
          {
            title: (
              <span>
                库存锁定处理
                <Tooltip title={LOCK_HANDLING_HELP} placement="bottom" overlayStyle={{ maxWidth: 440 }}>
                  <QuestionCircleOutlined className="nonstd-settlement-lock-help" style={{ marginLeft: 4, color: '#8c8c8c', cursor: 'help' }} />
                </Tooltip>
              </span>
            ),
            width: 180,
            render: (_: any, item) => (
              <div className="nonstd-settlement-block">
                <div style={SUB_ROW}>
                  <span style={SUB_LABEL}>释放</span>
                  <InputNumber size="small" readOnly controls={false} style={SUB_INPUT_AUTO} className="nonstd-settlement-release" value={item.row.keep - item.row.hold} />
                </div>
                <div style={SUB_ROW}>
                  <Tooltip title="不变的部分里，有多少件转业务留存占用；其余释放"><span style={SUB_LABEL}>转业务留存占用</span></Tooltip>
                  <InputNumber size="small" style={SUB_INPUT} className="nonstd-settlement-hold" min={0} max={item.row.keep} precision={0} value={item.row.hold} onChange={v => setHold(item.iu_id, Number(v) || 0)} />
                </div>
                <div style={SUB_ROW}>
                  <span style={SUB_LABEL}>消耗</span>
                  <InputNumber size="small" readOnly controls={false} style={SUB_INPUT_AUTO} className="nonstd-settlement-consume" value={item.row.convert + item.row.destroy} />
                </div>
              </div>
            ),
          },
        ]}
      />
      {problems.length > 0 ? <Alert type="warning" showIcon style={{ marginTop: 8 }} message={problems[0]} /> : null}
      <Space wrap style={{ marginTop: 10 }}>
        <Button type="primary" loading={busy} disabled={!dirty} onClick={() => { void save(); }}>保存草稿</Button>
        {dirty ? <Tag color="warning">有未保存的修改</Tag> : null}
        {touchedCount === 0 && !dirty ? <span style={{ color: '#8c8c8c' }}>没有设置任何处理：提交终审时锁定全部释放</span> : null}
      </Space>

      {targetModal}

      <Modal
        title="终审操作预览（库存结算 + 容器分配）"
        open={previewOpen}
        width={900}
        onCancel={() => setPreviewOpen(false)}
        destroyOnClose
        footer={[
          <Button key="close" onClick={() => setPreviewOpen(false)}>返回修改</Button>,
          <Button
            key="ok"
            type="primary"
            loading={busy}
            disabled={!preview || preview.empty || preview.blockers.length > 0 || (preview.warnings.length > 0 && !acknowledged)}
            onClick={() => { void doConfirm(); }}
          >
            确认终审操作
          </Button>,
        ]}
      >
        {previewBody}
        {preview && !preview.empty && preview.warnings.length > 0 ? (
          <div style={{ marginTop: 12 }}>
            <Checkbox checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)}>我已知晓以上警告，仍然确认</Checkbox>
          </div>
        ) : null}
        {preview && !preview.empty ? (
          <Input.TextArea
            style={{ marginTop: 10 }}
            rows={2}
            maxLength={300}
            placeholder="确认说明（选填，例如强制确认的原因）"
            value={confirmNote}
            onChange={e => setConfirmNote(e.target.value)}
          />
        ) : null}
        <div style={{ marginTop: 10, fontSize: 12, color: '#8c8c8c' }}>确认后不会立刻改库存；点页面右侧的“终审提交”时，系统会在同一个事务里先应用库存结算再生成收费记录，任何一步失败都会整体回滚。</div>
      </Modal>
    </div>
  );
});

export default SettlementPanel;
